import React, { useState, useEffect } from "react";

import LoadingOverlay from "../LoadingOverlay";
import RollforwardTable from "../rollforward/RollforwardTable";
import SingleSelectSortField from "../rollforward/SingleSelectSortField";
import DateSelectField from "./DateSelectField";
import MultiSelectFilter from "./MultiSelectFilter";
import WaterfallChart from "./WaterfallChart";
import { getDefaultStartDate, getDefaultEndDate } from "./dateUtils";

const WaterfallSection = ({ props, onLoad }) => {
    const sortOptions = [
        { id: "customer", name: "Customer" },
        { id: "effective_date", name: "Effective Date" },
        { id: "adjustment_type", name: "CARR Type" },
        { id: "churn_reason", name: "Churn Reason" },
        { id: "comments", name: "Comments" },
        { id: "carr", name: "CARR Amount" },
    ];

    const sortDirections = [
        { id: "ASC", name: "Ascending" },
        { id: "DESC", name: "Descending" },
    ];

    const [sortField, setSortField] = useState(sortOptions[5]);
    const [sortDirectionField, setSortDirectionField] = useState(sortDirections[1]);

    const [productOptions, setProductOptions] = useState([]);
    const [productGroupOptions, setProductGroupOptions] = useState([]);
    const [businessUnitOptions, setBusinessUnitOptions] = useState([]);

    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedProductGroups, setSelectedProductGroups] = useState([]);
    const [selectedBusinessUnits, setSelectedBusinessUnits] = useState([]);

    const [startDate, setStartDate] = useState(getDefaultStartDate());
    const [endDate, setEndDate] = useState(getDefaultEndDate());

    const today = new Date();
    const showCarrBanner = today.getDate() <= 8;

    const [reportData, setReportData] = useState(null);
    const [drillDownData, setDrillDownData] = useState(null);

    const [adjType, setAdjType] = useState("");

    const [isLoading, setIsLoading] = useState(true);

    const [isDrillDownLoading, setIsDrillDownLoading] = useState(true);

    // The chart loads once on mount (after product options arrive); after that,
    // filter/date changes are applied only when the refresh button is clicked.
    const [hasInitialLoad, setHasInitialLoad] = useState(false);

    const loadWaterfall = () => {
        // Wait until product options have loaded; an empty selection is a valid
        // state now ("ALL"), so only skip before the options arrive.
        if (productOptions.length === 0) {
            console.log("[Waterfall] Skipping load - product options not loaded yet");
            return;
        }

        // Send "NONE" when a dimension isn't narrowed (nothing selected, or every
        // option selected) so the RESTlet skips that filter instead of querying
        // against an empty or whole ID list. Only a partial selection sends IDs.
        const toFilterParam = (selection, options) =>
            selection.length === 0 || selection.length === options.length
                ? "NONE"
                : selection.map((item) => item.id);

        // A single getwaterfall response carries BOTH the chart payload and the
        // drilldown rows, so fetch once and populate both instead of issuing two
        // identical requests.
        setIsLoading(true);
        setIsDrillDownLoading(true);
        props
            .fetchData("getwaterfall", {
                startDate: startDate,
                endDate: endDate,
                products: toFilterParam(selectedProducts, productOptions),
                productGroups: toFilterParam(selectedProductGroups, productGroupOptions),
                businessUnits: toFilterParam(selectedBusinessUnits, businessUnitOptions),
                sortField: sortField.id,
                sortDirection: sortDirectionField.id,
                adjType: adjType,
            })
            .then((data) => {
                setReportData(data);
                setDrillDownData(formatTableData(data.drillDownData));
                setIsLoading(false);
                setIsDrillDownLoading(false);
                if (onLoad) onLoad();
            })
            .catch((err) => {
                // The global ApiErrorModal surfaces the user-facing message; here we
                // just clear the loading state so the chart area stops spinning.
                console.error("Failed to fetch report data:", err);
                setIsLoading(false);
                setIsDrillDownLoading(false);
                if (onLoad) onLoad();
            });
    };

    const loadProductData = () => {
        props
            .fetchData("getproducts")
            .then((data) => {
                setProductOptions(data.products);
                setProductGroupOptions(data.productGroups);
                setBusinessUnitOptions(data.businessUnits || []);
                // Start with no selection on load; empty maps to "NONE" (unfiltered).
                setSelectedProducts([]);
                setSelectedProductGroups([]);
                setSelectedBusinessUnits([]);
                // Trigger the one-time initial chart load now that options exist
                setHasInitialLoad(true);
            })
            .catch(() => {
                console.error("Failed to fetch product data");
            });
    };

    // Initial chart load once product options are available. Filter/date changes
    // thereafter are applied only when the refresh button is clicked.
    useEffect(() => {
        if (hasInitialLoad) {
            loadWaterfall();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasInitialLoad]);

    // Chart drill-down (bar click) and table sorting reload immediately.
    useEffect(() => {
        if (hasInitialLoad) {
            loadWaterfall();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adjType, sortField, sortDirectionField]);

    useEffect(() => {
        loadProductData();
    }, []);

    const formatTableData = (data) => {
        return data.map((item) => {
            const formattedItem = {
                ...item,
                carr:
                    typeof item.carr === "number"
                        ? `${item.carr < 0 ? "-" : ""}$${Math.abs(item.carr).toLocaleString(
                              "en-US",
                              {
                                  minimumFractionDigits: 2,
                              }
                          )}`
                        : item.carr,
            };
            return formattedItem;
        });
    };

    // The three filters are mutually exclusive: applying a selection on one resets
    // the other two to "all", so the report is only ever narrowed by a single
    // dimension at a time (products, product groups, or business units). This avoids
    // contradictory combinations (e.g. a product that isn't in the chosen business
    // unit) that would otherwise return an empty report.
    const handleProductsChange = (selection) => {
        setSelectedProducts(selection);
        setSelectedProductGroups([]);
        setSelectedBusinessUnits([]);
    };

    const handleProductGroupsChange = (selection) => {
        setSelectedProductGroups(selection);
        setSelectedProducts([]);
        setSelectedBusinessUnits([]);
    };

    const handleBusinessUnitsChange = (selection) => {
        setSelectedBusinessUnits(selection);
        setSelectedProducts([]);
        setSelectedProductGroups([]);
    };

    return (
        <div className="mt-32">
            <h2>CARR Rollforward</h2>
            <div className="flex flex-wrap items-end justify-center gap-4 p-4">
                <DateSelectField label="Start Date" value={startDate} onChange={setStartDate} />
                <DateSelectField label="End Date" value={endDate} onChange={setEndDate} />
                <MultiSelectFilter
                    label="Products"
                    options={productOptions}
                    selected={selectedProducts}
                    onChange={handleProductsChange}
                />
                <MultiSelectFilter
                    label="Product Groups"
                    options={productGroupOptions}
                    selected={selectedProductGroups}
                    onChange={handleProductGroupsChange}
                />
                <MultiSelectFilter
                    label="Business Units"
                    options={businessUnitOptions}
                    selected={selectedBusinessUnits}
                    onChange={handleBusinessUnitsChange}
                />

                <div className="flex flex-col">
                    <label className="invisible mb-1 block text-sm font-medium text-gray-700">
                        Refresh
                    </label>
                    <button
                        onClick={loadWaterfall}
                        title="Refresh"
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#0056b3")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#007bff")}
                        style={{
                            padding: "10px",
                            borderRadius: "4px",
                            backgroundColor: "#007bff",
                            color: "#fff",
                            border: "none",
                            cursor: "pointer",
                            transition: "background-color 0.2s",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <i className="fas fa-sync-alt"></i>
                    </button>
                </div>
            </div>

            <div className="relative mt-4 flex min-h-[400px] items-center justify-center">
                {reportData && <WaterfallChart reportData={reportData} setAdjType={setAdjType} />}
                {isLoading && <LoadingOverlay contained text="Loading report data..." />}
            </div>
            {showCarrBanner && (
                <div className="mt-8 rounded bg-yellow-100 p-4 text-center text-sm text-yellow-800">
                    CARR metrics are generally finalized by the 5th business day of each month.
                </div>
            )}
            <div className="mt-4 flex min-h-screen w-full px-8">
                <div className="my-16 w-full">
                    <div className="mb-4 flex justify-end gap-x-5">
                        <SingleSelectSortField
                            label="Sort By"
                            options={sortOptions}
                            selected={sortField}
                            onChange={(option) => setSortField(option)}
                        />
                        <SingleSelectSortField
                            label="Direction"
                            options={sortDirections}
                            selected={sortDirectionField}
                            onChange={(option) => setSortDirectionField(option)}
                        />
                    </div>

                    {isDrillDownLoading && (
                        <div className="flex min-h-[400px] items-center justify-center">
                            <LoadingOverlay text="Loading drilldown data..." />
                        </div>
                    )}
                    {!isDrillDownLoading && drillDownData && (
                        <RollforwardTable
                            reportData={drillDownData}
                            sortField={sortField}
                            setSortField={setSortField}
                            sortDirectionField={sortDirectionField}
                            setSortDirectionField={setSortDirectionField}
                            sortOptions={sortOptions}
                            sortDirections={sortDirections}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default WaterfallSection;
